import {createFeatureSelector, createSelector} from '@ngrx/store';
import {
  selectFiltersAndToolsProperty,
  selectNGPReportsFiltered,
  selectNGPReportsMenuDataByStoreId,
  selectNGPVisibleFields,
  selectVatRateBySelectedStore,
} from '../../../features/ngp-report/store/ngp-report.selectors';
import {SharedGridState} from './shared-grid.reducer';
import {IStore} from '../../../shared/shared-models/store/store';
import {IPriceBand, IPriceBandPerStore} from '../../../shared/shared-models/price-banding/price-band';
import {NGPReport} from '../../../shared-utilities/models-old/ngp-reports/ngp-report';
import {
  applyPriceBanding,
  PRICE_BANDING_COLUMNS_FOR_NGP_REPORTS,
} from '../../../shared/shared-utils/price/price-banding.utils';
import {StockItem, VatRates} from '../../../shared-utilities/models-old/datastructures';
import {
  selectAllStockItemsForCurrentSelectedStore,
  selectEditedItems,
  selectEnablePriceBandingForStockManager,
  selectMultiStoreStockItemsWithEdits,
  selectStockManagerMenuDataByStoreId,
} from '../../../features/stock-manager/store/stock-manager.selectors';
import {applyMultiStorePriceBanding} from '../../../features/stock-manager/utils/stock-manager-multi-store.utils';
import {
  selectAllUserStores,
  selectCurrentNavigationObj,
  selectSelectedStore,
} from '../../../features/user-access/user-stores-store/user-store.selectors';
import {INavigate} from '../../../features/user-access/models';
import {NavigationPageEnum} from '../../../features/user-access/models/navigations.enums';

export const selectSharedGridState = createFeatureSelector<SharedGridState>('shared-grid');

//===============================================================================================================
// Export to CSV
//===============================================================================================================
export const selectExportToCSVSettings = createSelector(
  selectSharedGridState,
  (state: SharedGridState) => state.exportToCSVSettings,
);

//===============================================================================================================
// Get Table Nav Settings
//===============================================================================================================
export const selectGridHeaderMenuDataByCurrentPage = createSelector(
  selectCurrentNavigationObj,
  selectNGPReportsMenuDataByStoreId,
  selectStockManagerMenuDataByStoreId,
  (
    navigation: { currentSelectedPage: INavigate },
    ngpMenuData,
    stockManagerMenuData,
  ) => {
    const currentPage = navigation.currentSelectedPage.currentPage;
    switch (currentPage) {
      case NavigationPageEnum.NGPReport:
        return ngpMenuData;
      case 'stock-manager':
        return stockManagerMenuData;
      default:
        return {};
    }
  },
);

export const selectSharedGridVisibleFieldsByCurrentPage = createSelector(
  selectCurrentNavigationObj,
  (navigation: { currentSelectedPage: INavigate }) => {
    const currentPage = navigation.currentSelectedPage.currentPage;
    switch (currentPage) {
      case 'ngp-report':
        return selectNGPVisibleFields;
      case 'stock-manager':
        return {};
      default:
        return {};
    }
  },
);

export const selectTableNavSettings = createSelector(
  selectSharedGridState,
  (state: SharedGridState) => state.tableNavSettings,
);

//===============================================================================================================
// Price Banding
//===============================================================================================================

export const selectSharedGridVatRateBySelectedStore = createSelector(
  selectSharedGridState,
  selectSelectedStore,
  (state: SharedGridState, selectedUserStore: IStore) => state.vatRates[selectedUserStore.storeId] || {},
);
export const selectAllSharedGridVatRates = createSelector(
  selectSharedGridState,
  (state: SharedGridState): Record<string, VatRates> => state.vatRates || {},
);
export const selectIsGetPriceBandingPerStoreLoading = createSelector(
  selectSharedGridState,
  (state: SharedGridState) => state.isPriceBandingPerStoreLoading,
);

export const selectPriceBandingForAllStores = createSelector(
  selectSharedGridState,
  (state: SharedGridState) => state.priceBandsPerStore,
);

export const selectPriceBandingForUserSelectedStore = createSelector(
  selectPriceBandingForAllStores,
  selectSelectedStore,
  (priceBands: IPriceBandPerStore, userStore: IStore): IPriceBand[] => {
    return priceBands[userStore.storeId];
  },
);

export const selectNGPReportsPriceBanded = createSelector(
  selectPriceBandingForUserSelectedStore,
  selectNGPReportsFiltered,
  selectFiltersAndToolsProperty<boolean>('isApplyPriceBandingOn'),
  selectSelectedStore,
  selectVatRateBySelectedStore,
  (priceBands: IPriceBand[], ngpReports: NGPReport[], isPriceBanding, userStore, vatRates: VatRates): NGPReport[] => {
    if (isPriceBanding) {
      return applyPriceBanding<NGPReport[]>(ngpReports, priceBands, PRICE_BANDING_COLUMNS_FOR_NGP_REPORTS, userStore, vatRates);
    } else {
      return ngpReports;
    }
  },
);

export const selectStockManagerPriceBanded = createSelector(
  selectPriceBandingForUserSelectedStore,
  selectAllStockItemsForCurrentSelectedStore,
  selectEnablePriceBandingForStockManager,
  selectSelectedStore,
  selectSharedGridVatRateBySelectedStore,
  selectEditedItems,
  (
    priceBands: IPriceBand[],
    stockItems: StockItem[],
    isPriceBanding: boolean,
    userStore: IStore,
    vatRates: VatRates,
    editedItems: Record<string, StockItem> | undefined,
  ): StockItem[] => {
    let items: StockItem[] = stockItems || [];
    if (editedItems && Object.keys(editedItems).length > 0) {
      items = items.map((stockItem: StockItem) => {
        const editedItem = editedItems[String(stockItem.code)] ?? editedItems[stockItem.code];
        if (!editedItem) {
          return stockItem;
        }
        const mergedItem = {
          ...stockItem,
          ...editedItem,
        };
        return {
          ...mergedItem,
          isEdited: editedItem.isEdited ?? true,
        };
      });
    }

    if (isPriceBanding) {
      return applyPriceBanding(
        items,
        priceBands,
        PRICE_BANDING_COLUMNS_FOR_NGP_REPORTS,
        userStore,
        vatRates,
      ) || [];
    }
    return items;
  },
);

export const selectMultiStoreStockItemsPriceBanded = createSelector(
  selectMultiStoreStockItemsWithEdits,
  selectEnablePriceBandingForStockManager,
  selectPriceBandingForAllStores,
  selectAllSharedGridVatRates,
  selectAllUserStores,
  (
    stockItems: StockItem[],
    isPriceBanding: boolean,
    priceBands: IPriceBandPerStore,
    vatRates: Record<string, VatRates>,
    stores: IStore[],
  ): StockItem[] => applyMultiStorePriceBanding(stockItems, isPriceBanding, priceBands, vatRates, stores || []),
);



